import type { MorphemStammExerciseConfig, MorphemStammRunSettings } from "@calega/content";
import type { QualitativeEvidenceRef } from "../analysis-contract";
import { accuracy, qualitativeEvidenceRefs } from "../analysis-utils";
import { randomGenerator } from "../run-random";
import { normalizedItemText, pickRunItems, shuffled, type RunSelection } from "../run-selection";

export type MorphemGrapheme = MorphemStammExerciseConfig["items"][number]["graphemes"][number];

export interface MorphemStammRunItem {
  id: string;
  sourceId: string;
  before: string;
  after: string;
  targetWord: string;
  lemma: string;
  graphemeOptions: Array<{ id: MorphemGrapheme; text: MorphemGrapheme }>;
  expectedGrapheme: "ä" | "äu" | "Ä" | "Äu";
  rootOptions: Array<{ id: string; text: string }>;
  difficulty: "1" | "2" | "3";
  graphemePattern: "umlaut_derivation";
  morphemContext: "morphemic_root_validation" | "prefix_validation" | "suffix_validation";
}

export interface MorphemStammRun {
  seed: string;
  generatorVersion: 1 | 2;
  exerciseType: "morphem-stamm";
  settings: MorphemStammRunSettings;
  items: MorphemStammRunItem[];
}

export interface MorphemStammAnswer {
  itemKey: string;
  selectedGrapheme: string;
  selectedRoot: string;
  reassignedCount: number;
  decisionPath: Array<"grapheme" | "root">;
}

export type MorphemErrorSubtype = "confirmed_selection" | "lucky_guess_overgeneralization" | "phonetic_surface_bias" | "visual_homophone_error";

export function classifyMorphemAnswer(item: MorphemStammRunItem, selectedGrapheme: string, selectedRoot: string) {
  const graphemeCorrect = selectedGrapheme.toLocaleLowerCase("de-DE") === item.expectedGrapheme.toLocaleLowerCase("de-DE");
  const rootCorrect = selectedRoot.toLocaleLowerCase("de-DE") === item.lemma.toLocaleLowerCase("de-DE");
  const errorSubtype: MorphemErrorSubtype = graphemeCorrect
    ? rootCorrect ? "confirmed_selection" : "lucky_guess_overgeneralization"
    : rootCorrect ? "phonetic_surface_bias" : "visual_homophone_error";
  return { graphemeCorrect, rootCorrect, correct: graphemeCorrect && rootCorrect, errorSubtype };
}

export function generateMorphemStammRun(config: MorphemStammExerciseConfig, settings: MorphemStammRunSettings, seed: string, selection?: RunSelection): MorphemStammRun {
  const random = randomGenerator(seed);
  const total = selection?.count ?? (settings.mode === "time" ? Math.max(120, settings.durationSeconds * 2) : settings.count);
  const selected = pickRunItems(config.items, total, random, (item) => [
    normalizedItemText(item.before),
    normalizedItemText(item.targetWord),
    normalizedItemText(item.after),
    normalizedItemText(item.lemma),
    normalizedItemText(item.expectedGrapheme),
    [...item.graphemes].map(normalizedItemText).sort().join(","),
    [...item.distractors].map(normalizedItemText).sort().join(","),
  ].join("|"), selection?.unique);
  const items = selected.map((item, index) => ({
    ...item,
    id: `morphem-stamm-${index + 1}-${item.id}`,
    sourceId: item.id,
    graphemeOptions: shuffled(item.graphemes.map((text) => ({ id: text, text })), random),
    rootOptions: shuffled([
      { id: item.lemma, text: item.lemma },
      ...item.distractors.map((text) => ({ id: text, text })),
    ], random),
  }));
  return { seed, generatorVersion: 2, exerciseType: "morphem-stamm", settings, items };
}

export interface MorphemStammAnalysis {
  schemaVersion: "word-diagnostics-v1.2";
  exerciseType: "morphem-stamm";
  answered: number;
  total: number;
  graphemeAccuracy: number | null;
  rootAccuracy: number | null;
  coupledAccuracy: number | null;
  timeline: Array<{ order: number; wordSurface: string; wordLemma: string; selectedGrapheme: string; selectedRoot: string; errorSubtype: MorphemErrorSubtype; reassignedCount: number }>;
  hypotheses: Array<{
    ruleId: "morphem-stamm.phonetic_surface_bias" | "morphem-stamm.visual_homophone_error" | "morphem-stamm.lucky_guess" | "morphem-stamm.reflective_reassignment";
    status: "triggered" | "not_triggered" | "insufficient_data";
    relevantCount: number;
    evidenceCount: number;
    evidenceRefs: QualitativeEvidenceRef[];
  }>;
}

export function analyzeMorphemStammRun(run: MorphemStammRun, answers: MorphemStammAnswer[]): MorphemStammAnalysis {
  const latest = new Map(answers.map((answer) => [answer.itemKey, answer]));
  const rows = run.items.flatMap((item, index) => {
    const answer = latest.get(item.id);
    if (!answer) return [];
    return [{ item, answer, order: index + 1, result: classifyMorphemAnswer(item, answer.selectedGrapheme, answer.selectedRoot) }];
  });
  const graphemeCorrect = rows.filter(({ result }) => result.graphemeCorrect).length;
  const rootCorrect = rows.filter(({ result }) => result.rootCorrect).length;
  const coupledCorrect = rows.filter(({ result }) => result.correct).length;
  const countSubtype = (subtype: MorphemErrorSubtype) => rows.filter(({ result }) => result.errorSubtype === subtype).length;
  const phonetic = countSubtype("phonetic_surface_bias");
  const visual = countSubtype("visual_homophone_error");
  const lucky = countSubtype("lucky_guess_overgeneralization");
  const reassignedTokens = rows.filter(({ answer }) => answer.reassignedCount > 0).length;
  const reassignedTotal = rows.reduce((sum, { answer }) => sum + answer.reassignedCount, 0);
  const patternStatus = (evidence: number, additionalGuard = true) => rows.length < 3 ? "insufficient_data" as const : additionalGuard && evidence / rows.length >= 0.3 ? "triggered" as const : "not_triggered" as const;
  const reassignmentStatus = rows.length < 2 ? "insufficient_data" as const : reassignedTotal >= 3 || reassignedTokens / rows.length >= 0.3 ? "triggered" as const : "not_triggered" as const;
  return {
    schemaVersion: "word-diagnostics-v1.2",
    exerciseType: "morphem-stamm",
    answered: rows.length,
    total: run.items.length,
    graphemeAccuracy: accuracy(graphemeCorrect, rows.length),
    rootAccuracy: accuracy(rootCorrect, rows.length),
    coupledAccuracy: accuracy(coupledCorrect, rows.length),
    timeline: rows.map(({ item, answer, order, result }) => ({ order, wordSurface: item.targetWord, wordLemma: item.lemma, selectedGrapheme: answer.selectedGrapheme, selectedRoot: answer.selectedRoot, errorSubtype: result.errorSubtype, reassignedCount: answer.reassignedCount })),
    hypotheses: [
      { ruleId: "morphem-stamm.phonetic_surface_bias", status: patternStatus(phonetic, accuracy(rootCorrect, rows.length)! >= 0.5), relevantCount: rows.length, evidenceCount: phonetic, evidenceRefs: qualitativeEvidenceRefs(rows.filter(({ result }) => result.errorSubtype === "phonetic_surface_bias").map(({ item }) => item.id)) },
      { ruleId: "morphem-stamm.visual_homophone_error", status: patternStatus(visual), relevantCount: rows.length, evidenceCount: visual, evidenceRefs: qualitativeEvidenceRefs(rows.filter(({ result }) => result.errorSubtype === "visual_homophone_error").map(({ item }) => item.id)) },
      { ruleId: "morphem-stamm.lucky_guess", status: patternStatus(lucky), relevantCount: rows.length, evidenceCount: lucky, evidenceRefs: qualitativeEvidenceRefs(rows.filter(({ result }) => result.errorSubtype === "lucky_guess_overgeneralization").map(({ item }) => item.id)) },
      { ruleId: "morphem-stamm.reflective_reassignment", status: reassignmentStatus, relevantCount: rows.length, evidenceCount: reassignedTokens, evidenceRefs: qualitativeEvidenceRefs(rows.filter(({ answer }) => answer.reassignedCount > 0).map(({ item }) => item.id)) },
    ],
  };
}
